import Button from "app/storybookComponents/Button";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { Card } from "app/shadcnComponents/ui/card";
import { faFireFlameCurved } from "@fortawesome/pro-light-svg-icons";
import SimpleBarChart from "app/storybookComponents/Charts/SimpleBarChart";
import SupportOrCelebrateDot from "../Tables/SupportOrCelebrateDot";
import {
  faArrowTrendUp,
  faArrowUpRight,
  faSparkles,
  faTrophy,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { cn } from "app/lib/utils";
import { getS } from "utils/helperFunctions";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getIndividualCheckInActivitySnapshot,
  selectGetIndividualCheckInActivitySnapshotStatus,
  selectIndividualCheckInActivitySnapshot,
} from "../slice";

import Loading from "app/storybookComponents/Loading";

const IndividualContributorWeeklyCheckInSnapshot: React.FC = () => {
  const dispatch = useAppDispatch();
  const individualCheckInActivitySnapshot = useAppSelector(
    selectIndividualCheckInActivitySnapshot
  );

  const getIndividualCheckInActivitySnapshotStatus = useAppSelector(
    selectGetIndividualCheckInActivitySnapshotStatus
  );

  useEffect(() => {
    dispatch(getIndividualCheckInActivitySnapshot());
  }, [dispatch]);

  const navigate = useNavigate();

  if (getIndividualCheckInActivitySnapshotStatus === "loading") {
    return <Loading />;
  }

  if (!individualCheckInActivitySnapshot) {
    return null;
  }

  const getTrendDirection = (
    scores: { overAllScore?: number | null }[]
  ): "up" | "down" | "stable" => {
    if (scores.length < 2) return "stable";
    // get average of first half and last half
    const halfIndex = Math.floor(scores.length / 2);
    const firstHalf = scores.slice(0, halfIndex);
    const lastHalf = scores.slice(halfIndex);
    const firstScore =
      firstHalf.reduce((acc, score) => acc + (score.overAllScore ?? 0), 0) /
      firstHalf.length;
    const lastScore =
      lastHalf.reduce((acc, score) => acc + (score.overAllScore ?? 0), 0) /
      lastHalf.length;
    if (lastScore > firstScore) return "up";
    if (lastScore < firstScore) return "down";
    return "stable";
  };

  const getPatternStatus = (
    pattern?:
      | "No Issues"
      | "Things Improving"
      | "Recent Challenge"
      | "May Need Support"
  ): {
    type: "celebrate" | "support";
    message: string;
  } => {
    if (
      pattern === "Things Improving" ||
      pattern === "Recent Challenge" ||
      pattern === "May Need Support"
    ) {
      return {
        type: "support",
        message: pattern,
      };
    }
    return {
      type: "celebrate",
      message: "No Issues",
    };
  };

  const getStreakBox = () => {
    const streakBars = Array.from({ length: 8 }, (_, index) => (
      <div
        key={index}
        style={{
          backgroundColor:
            index < individualCheckInActivitySnapshot.streak
              ? "var(--sapphire-100)"
              : "var(--charcoal-80)",
          height: "4px",
          width: "100%",
          borderRadius: "2px",
        }}
      />
    ));

    return (
      <Card className="p-[14px] flex gap-2 flex-col">
        <div className="flex gap-2 justify-content-between">
          <div className="flex gap-2">
            <FontAwesomeIcon icon={faFireFlameCurved} />
            <p>Streak</p>
          </div>
          <p>{individualCheckInActivitySnapshot.streak} check-in{getS(individualCheckInActivitySnapshot.streak)}</p>
        </div>
        <div className="flex gap-1 mt-3">{streakBars}</div>
      </Card>
    );
  };

  const getWeeksTrendBox = () => {
    const last4Scores =
      individualCheckInActivitySnapshot.overallScores.slice(-4);
    const trendDirection = getTrendDirection(last4Scores);

    const trendLabelsAndClassName = {
      up: { label: "Trending up", className: "" },
      down: { label: "Trending down", className: "rotate-90" },
      stable: { label: "Holding steady", className: "rotate-45" },
    };

    const bars =
      last4Scores.length > 0
        ? last4Scores.map((score, index) => ({
            value: score.overAllScore ?? 0.25,
            color:
              index === last4Scores.length - 1
                ? "var(--sapphire-100)"
                : undefined,
          }))
        : [{ value: 0.25 }, { value: 0.25 }, { value: 0.25 }, { value: 0.25 }];

    const { label, className } = trendLabelsAndClassName[trendDirection];

    return (
      <Card className="p-[14px] flex gap-2 flex-col">
        <div className="flex gap-2 justify-between items-center">
          <div className="flex gap-2">
            <FontAwesomeIcon icon={faArrowTrendUp} />
            <p>Last 4 Check-ins Trend</p>
          </div>
        </div>
        <SimpleBarChart
          bars={bars}
          defaultColor="var(--sapphire-30)"
          maxHeight={50}
          gap={6}
          maxValue={5}
        />
        {last4Scores.length > 0 && (
          <div className="flex gap-1 items-center text-sm bg-[var(--charcoal-05)] rounded-lg p-1">
            <FontAwesomeIcon
              icon={faArrowUpRight}
              className={cn("text-[var(--charcoal-80)]", className)}
            />
            <span>{label}</span>
          </div>
        )}
      </Card>
    );
  };

  const getYourPatternBox = () => {
    const patternStatus = getPatternStatus(
      individualCheckInActivitySnapshot.pattern
    );

    return (
      <Card className="p-[14px] flex gap-2 flex-col">
        <div className="flex gap-2">
          <FontAwesomeIcon icon={faWavePulse} />
          <p>Your Pattern</p>
        </div>
        <div className="flex gap-2 bg-[var(--charcoal-05)] rounded-lg p-2">
          <div className="mt-[4px]">
            <SupportOrCelebrateDot
              type={patternStatus.type}
              score={patternStatus.type === "celebrate" ? 10 : 1}
            />
          </div>
          <div>
            <p className="font-semibold">{patternStatus.message}</p>
            {individualCheckInActivitySnapshot.issuesRaised.length > 0 &&
            individualCheckInActivitySnapshot.pattern !== "No Issues" ? (
              <ul className="list-disc ps-0">
                {individualCheckInActivitySnapshot.issuesRaised?.map(
                  (issue) => (
                    <li key={issue}>
                      <p className="text-sm text-gray-600">{issue}</p>
                    </li>
                  )
                )}
              </ul>
            ) : (
              <p className="text-sm text-gray-600">
                No concerns raised in your last 4 check-ins
              </p>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const getYourWinsBox = () => {
    const getWinBox = (text: string, key: number) => (
      <div key={key} className="flex gap-2 bg-gold-10 rounded-lg p-2">
        <FontAwesomeIcon
          icon={faSparkles}
          className="mt-[3px] text-[var(--gold-100)]"
        />
        <p className="text-sm">{text}</p>
      </div>
    );

    const displayedWins = individualCheckInActivitySnapshot.wins.slice(0, 5);

    return (
      <Card className="p-[14px] flex gap-2 flex-col">
        <div className="flex gap-2">
          <FontAwesomeIcon icon={faTrophy} className="text-[var(--gold-100)]" />
          <p>Your Recent Wins</p>
        </div>
        {displayedWins.length > 0 ? (
          displayedWins.map((win, index) => getWinBox(win, index))
        ) : (
          <p className="text-sm text-gray-500 italic">
            {individualCheckInActivitySnapshot.hasCompletedPreviousCheckIn
              ? "No wins recorded in your last check-in"
              : "User has not completed a previous check-in"}
          </p>
        )}
      </Card>
    );
  };
  const getContent = () => {
    return (
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2">
        {getStreakBox()}
        {getWeeksTrendBox()}
        {getYourPatternBox()}
        {getYourWinsBox()}
      </div>
    );
  };

  return (
    <Card className="activity-monitor-container card">
      <div className="activity-monitor-container__header">
        <div className="activity-monitor-container__title">
          <h2>Your {WEEKLY_CHECK_IN_FEATURE_NAME}s</h2>
          <div>
            <FontAwesomeIcon icon={["far", "sparkles"]} />
            <span className="label-tag info-sapphire">Powered by AI</span>
          </div>
        </div>
      </div>
      {getContent()}
      <Button
        variant="tertiary-blue"
        onClick={() => {
          navigate("/DirectReportsWeeklyCheckin");
        }}
        className="activity-monitor-container__footer-button"
      >
        View activity{" "}
        <FontAwesomeIcon icon={["fas", "arrow-right"]} className="ms-2" />
      </Button>
    </Card>
  );
};

export default IndividualContributorWeeklyCheckInSnapshot;
